import { Fragment, memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Eye, Folder, FolderOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { parentQualifiesForFocusZone } from '@/lib/browse-folder-focus'
import { folderPathToNameSegmentsUnderRoot, normalizeExplorerPath } from '@/lib/explorer-tree-focus'
import { isOpenInTabs, type OpenTabIndex } from '@/lib/open-tab-match'
import {
  browseLevelFocusGutterLeftFromRowPad,
  wsNavRowPadLeftPx,
} from '@/components/layout/left-sidebar.components'
import { testIds } from '@/lib/test-ids'
import {
  BrowseLevelFocusRowControl,
  BrowseLevelFocusZone,
  type BrowseLevelFocusCoordinator,
} from '@/components/features/browse-tree-level-focus'
import { toast } from 'sonner'
import { useBrowseFolderFocus } from '@/providers/browse-folder-focus-provider'
import {
  useBrowseExpandedContext,
  useBrowsePathExpanded,
} from '@/providers/browse-expanded-provider'

/** Delay folder row single-click so double-click can drill; chevron expand is immediate. */
const FOLDER_CLICK_DELAY_MS = 180

/** Keep "Show hidden" row until child row expand/collapse animations finish (avoids layout jump). */
const BROWSE_CHILD_STAGGER_MS = 18
const BROWSE_CHILD_STAGGER_CAP = 12
const BROWSE_CHILD_EXPAND_MS = 260
const BROWSE_SHOW_HIDDEN_LAYOUT_MS =
  BROWSE_CHILD_EXPAND_MS + BROWSE_CHILD_STAGGER_CAP * BROWSE_CHILD_STAGGER_MS + 48

export type ExplorerFolderTreeNode = {
  id: string
  name: string
  type: 'folder' | 'file'
  path: string
  children?: ExplorerFolderTreeNode[]
  metadata?: {
    maxUpdatedAtMs?: number
    documentCount?: number
    folderCount?: number
    totalChildren?: number
    extension?: string
    documentType?: string
    kind?: string
  }
}

type ExplorerInlineFolderNodeProps = {
  node: ExplorerFolderTreeNode
  depth?: number
  treeRoot: string
  wsColor: string
  activeDocPath: string | null
  /** Prototype only; production uses BrowseExpandedProvider + per-path store. */
  expandedNodes?: Set<string>
  onToggleExpand?: (path: string) => void
  onNodeClick: (path: string, type: 'folder' | 'file', event: React.MouseEvent) => void
  onNodeDoubleClick?: (path: string, type: 'folder' | 'file', event: React.MouseEvent) => void
  onContextMenu?: (node: ExplorerFolderTreeNode, event: React.MouseEvent) => void
  /** Drop local .md/.txt onto a folder row */
  onFolderNativeFileDrop?: (folderPath: string, files: File[]) => void
  /** When set, folder double-click drills; single-click is delayed to allow double-click. */
  onDrillFocus?: (nameSegmentsFromTreeRoot: string[]) => void
  /** True when drill-down is active (tighter row padding + file tooltips). */
  drilledDown?: boolean
  /** Paths of open document tabs (for focus browse ancestry). */
  openTabPaths?: string[]
  /** Path + id index for open-echo (UUID tabs without filePath). */
  openTabIndex?: OpenTabIndex
  /** When set, child rows can show per-row focus collapse/expand control. */
  levelFocus?: BrowseLevelFocusCoordinator | null
  /** Parent row pad-left so children always indent one level further. */
  parentPadLeft?: number
  /** Stable focus-zone gutter for child pad (parent qualifies) before coordinator mounts. */
  parentZoneGutterLeftPx?: number
}

export const ExplorerInlineFolderNode = memo(function ExplorerInlineFolderNode({
  node,
  depth = 0,
  treeRoot,
  wsColor,
  activeDocPath,
  expandedNodes,
  onToggleExpand,
  onNodeClick,
  onNodeDoubleClick,
  onContextMenu,
  onFolderNativeFileDrop,
  onDrillFocus,
  drilledDown = false,
  openTabPaths = [],
  openTabIndex,
  levelFocus = null,
  parentPadLeft,
  parentZoneGutterLeftPx,
}: ExplorerInlineFolderNodeProps) {
  const browseFocus = useBrowseFolderFocus()
  const expandedFromStore = useBrowsePathExpanded(node.path)
  const expandedCtx = useBrowseExpandedContext()
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isNativeFileDropTarget, setIsNativeFileDropTarget] = useState(false)
  const expanded =
    expandedNodes !== undefined ? expandedNodes.has(node.path) : expandedFromStore
  const toggleExpand = onToggleExpand ?? expandedCtx?.togglePath ?? (() => {})

  useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
  }, [])

  const isFolder = node.type === 'folder'
  /** Folders with no tree children still reserve the chevron column so icons align with siblings. */
  const canExpand = isFolder && (node.children?.length ?? 0) > 0
  const nodePathNorm = normalizeExplorerPath(node.path)
  const activeNorm = activeDocPath ? normalizeExplorerPath(activeDocPath) : ''
  const isExact = !!activeNorm && activeNorm === nodePathNorm
  const isSelected = isExact || (!!activeNorm && activeNorm.startsWith(nodePathNorm + '/'))
  const isOpenElsewhere =
    !isExact &&
    (openTabIndex
      ? isOpenInTabs(openTabIndex, {
          path: node.path,
          documentId: node.type === 'file' ? node.id : null,
        })
      : openTabPaths.some(
          (p) => p === node.path || p.replace(/\/$/, '') === node.path.replace(/\/$/, ''),
        ))
  const fileIcon =
    node.type === 'file'
      ? getDocumentDisplayIcon(
          {
            kind: node.metadata?.kind,
            type: node.metadata?.documentType,
            filePath: node.path,
            extension: node.metadata?.extension,
          },
          { className: 'h-3.5 w-3.5 shrink-0' },
        )
      : null
  const FolderIcon = expanded ? FolderOpen : Folder

  const segmentsToHere = folderPathToNameSegmentsUnderRoot(treeRoot, node.path)
  const drillSegments =
    node.type === 'folder' && segmentsToHere.length > 0 ? segmentsToHere : null

  const handleRowClick = (e: React.MouseEvent) => {
    if (node.type !== 'folder' || !onDrillFocus) {
      onNodeClick(node.path, node.type, e)
      return
    }
    if (e.metaKey || e.ctrlKey) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
        clickTimerRef.current = null
      }
      onNodeClick(node.path, node.type, e)
      return
    }
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null
      onNodeClick(node.path, node.type, e)
    }, FOLDER_CLICK_DELAY_MS)
  }

  const handleRowDoubleClick = (e: React.MouseEvent) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }
    e.preventDefault()
    if (node.type === 'folder' && onDrillFocus && drillSegments) {
      onDrillFocus(drillSegments)
      return
    }
    onNodeDoubleClick?.(node.path, node.type, e)
  }

  /** Child rows receive levelFocus from parent zone (gutter + extra pad for parent column). */
  const zoneGutterLeftPx = levelFocus?.gutterLeftPx ?? parentZoneGutterLeftPx
  const inParentFocusZone = zoneGutterLeftPx != null

  const padLeft = wsNavRowPadLeftPx(
    depth,
    drilledDown && node.type === 'file',
    inParentFocusZone,
    zoneGutterLeftPx,
    parentPadLeft
  )

  const visibleChildIds = useMemo(() => {
    if (!browseFocus || !node.children?.length) return null
    const filtered = browseFocus.filterFolderChildren(
      node.children,
      node.path,
      activeDocPath,
      openTabPaths
    )
    return new Set(filtered.map((c) => c.id))
  }, [browseFocus, node.children, node.path, activeDocPath, openTabPaths])

  const hiddenBreakdown = useMemo(() => {
    if (!browseFocus || !node.children?.length) {
      return { folders: 0, files: 0, total: 0, label: '' }
    }
    return browseFocus.hiddenChildrenBreakdown(
      node.children,
      node.path,
      activeDocPath,
      openTabPaths
    )
  }, [browseFocus, node.children, node.path, activeDocPath, openTabPaths])

  const parentQualifies = Boolean(
    browseFocus &&
      node.children?.length &&
      parentQualifiesForFocusZone(node.children, browseFocus.focusMinChildren)
  )

  const parentRevealed =
    Boolean(browseFocus && isFolder && browseFocus.isBrowseParentRevealed(node.path))

  const totalChildCount = node.children?.length ?? 0
  const visibleChildCount =
    visibleChildIds === null ? totalChildCount : visibleChildIds.size
  const hasFilteredHidden = hiddenBreakdown.total > 0
  const hasActiveCap =
    totalChildCount > 0 && visibleChildCount < totalChildCount

  const levelFocusMode: 'expand' | 'collapse' | null =
    browseFocus?.focusBrowse &&
    isFolder &&
    expanded &&
    parentQualifies &&
    node.children &&
    browseFocus.parentQualifiesForFocus(node.children)
      ? parentRevealed
        ? 'collapse'
        : hasFilteredHidden
          ? 'expand'
          : hasActiveCap
            ? 'collapse'
            : null
      : null

  const [showHiddenLinkExiting, setShowHiddenLinkExiting] = useState<string | null>(null)
  const [showHiddenLinkEnterHold, setShowHiddenLinkEnterHold] = useState(false)
  const wasParentRevealedRef = useRef(parentRevealed)

  const showHiddenLink =
    browseFocus &&
    isFolder &&
    browseFocus.focusBrowse &&
    !parentRevealed &&
    hiddenBreakdown.total > 0

  const revealParentAnimated = useCallback(() => {
    if (!browseFocus || hiddenBreakdown.total <= 0) return
    setShowHiddenLinkExiting(hiddenBreakdown.label)
    browseFocus.revealBrowseParent(node.path)
    toast('Showing all items', {
      description: hiddenBreakdown.label,
      duration: 2200,
    })
  }, [browseFocus, hiddenBreakdown.label, hiddenBreakdown.total, node.path])

  const collapseParentAnimated = useCallback(() => {
    if (!browseFocus) return
    if (parentRevealed) {
      browseFocus.collapseBrowseParent(node.path)
      toast('Activity filter on', {
        description: `Recent items only under ${node.name || 'this folder'}`,
        duration: 2200,
      })
      return
    }
    if (!hasFilteredHidden && !hasActiveCap) return
    browseFocus.collapseBrowseParent(node.path)
    toast('Activity filter on', {
      description: `Recent items only under ${node.name || 'this folder'}`,
      duration: 2200,
    })
  }, [
    browseFocus,
    node.name,
    node.path,
    parentRevealed,
    hasFilteredHidden,
    hasActiveCap,
  ])

  useEffect(() => {
    if (!showHiddenLinkExiting) return
    const t = window.setTimeout(() => setShowHiddenLinkExiting(null), BROWSE_SHOW_HIDDEN_LAYOUT_MS)
    return () => window.clearTimeout(t)
  }, [showHiddenLinkExiting])

  useEffect(() => {
    const wasRevealed = wasParentRevealedRef.current
    wasParentRevealedRef.current = parentRevealed
    if (wasRevealed && !parentRevealed && showHiddenLink) {
      setShowHiddenLinkEnterHold(true)
      const t = window.setTimeout(
        () => setShowHiddenLinkEnterHold(false),
        BROWSE_SHOW_HIDDEN_LAYOUT_MS
      )
      return () => window.clearTimeout(t)
    }
    if (!showHiddenLink) setShowHiddenLinkEnterHold(false)
  }, [parentRevealed, showHiddenLink])

  useEffect(() => {
    if (!parentRevealed && !showHiddenLink) setShowHiddenLinkExiting(null)
  }, [parentRevealed, showHiddenLink])

  const showHiddenShellVisible =
    showHiddenLinkExiting !== null || (showHiddenLink && !showHiddenLinkEnterHold)
  const showHiddenLabel = showHiddenLinkExiting ?? hiddenBreakdown.label

  /** Stagger shells only when revealing hidden items, not on chevron expand. */
  const animateBrowseChildrenReveal = Boolean(
    browseFocus?.focusBrowse &&
      parentQualifies &&
      node.children?.length &&
      (parentRevealed ||
        showHiddenLinkExiting !== null ||
        showHiddenLinkEnterHold ||
        showHiddenShellVisible)
  )

  const parentFocusZoneEligible = Boolean(
    isFolder &&
      expanded &&
      parentQualifies &&
      browseFocus?.focusBrowse &&
      node.children &&
      browseFocus.parentQualifiesForFocus(node.children)
  )

  const parentZoneGutterForChildren = parentFocusZoneEligible
    ? browseLevelFocusGutterLeftFromRowPad(padLeft)
    : undefined

  const activeLevelFocus = useMemo((): BrowseLevelFocusCoordinator | null => {
    if (!levelFocusMode || !browseFocus?.focusBrowse) return null
    return {
      parentPath: node.path,
      wsColor,
      gutterLeftPx: browseLevelFocusGutterLeftFromRowPad(padLeft),
      mode: levelFocusMode,
      onAction: () => {
        if (levelFocusMode === 'expand') {
          revealParentAnimated()
        } else {
          collapseParentAnimated()
        }
      },
    }
  }, [
    levelFocusMode,
    browseFocus,
    node.path,
    wsColor,
    padLeft,
    revealParentAnimated,
    collapseParentAnimated,
  ])

  const renderTreeChild = (
    child: ExplorerFolderTreeNode,
    levelFocusProp: BrowseLevelFocusCoordinator | null,
    childIndex = 0
  ) => {
    const visible = visibleChildIds === null || visibleChildIds.has(child.id)
    const childNode = (
      <ExplorerInlineFolderNode
        node={child}
        depth={depth + 1}
        treeRoot={treeRoot}
        wsColor={wsColor}
        activeDocPath={activeDocPath}
        expandedNodes={expandedNodes}
        onToggleExpand={toggleExpand}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onContextMenu={onContextMenu}
        onFolderNativeFileDrop={onFolderNativeFileDrop}
        onDrillFocus={onDrillFocus}
        drilledDown={drilledDown}
        openTabPaths={openTabPaths}
        openTabIndex={openTabIndex}
        levelFocus={levelFocusProp}
        parentPadLeft={padLeft}
        parentZoneGutterLeftPx={parentZoneGutterForChildren}
      />
    )
    if (!animateBrowseChildrenReveal) {
      return visible ? childNode : null
    }
    const staggerMs = visible ? Math.min(childIndex, BROWSE_CHILD_STAGGER_CAP) * BROWSE_CHILD_STAGGER_MS : 0
    return (
      <div
        key={child.id}
        className={cn(
          'browse-focus-child-shell',
          visible && 'browse-focus-child-shell--visible'
        )}
        style={{ transitionDelay: `${staggerMs}ms` }}
        aria-hidden={!visible}
      >
        <div className="browse-focus-child-shell__inner">{childNode}</div>
      </div>
    )
  }

  const activityLabel =
    browseFocus && node.type === 'folder' ? browseFocus.activityDaysLabel(node.path) : null

  const nameEl =
    node.type === 'file' ? (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="ws-nav-row__label cursor-default">{node.name}</span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm break-all text-left">
          {node.name}
        </TooltipContent>
      </Tooltip>
    ) : (
      <>
        <span className="ws-nav-row__label min-w-0 truncate" title={node.name}>
          {node.name}
        </span>
        {activityLabel != null && (
          <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums ml-0.5">
            {activityLabel}
          </span>
        )}
      </>
    )

  return (
    <div className="min-w-0">
      <div
        className={cn(
          'relative min-w-0 browse-level-focus-row',
          inParentFocusZone && 'browse-level-focus-row--gutter'
        )}
        style={
          levelFocus
            ? ({
                '--browse-level-focus-gutter-left': `${levelFocus.gutterLeftPx}px`,
                '--ws-color': levelFocus.wsColor,
              } as React.CSSProperties)
            : undefined
        }
      >
        {inParentFocusZone && levelFocus && browseFocus?.focusBrowse ? (
          <BrowseLevelFocusRowControl coordinator={levelFocus} />
        ) : null}
        <div
          data-node-path={node.path}
          data-testid={testIds.explorerNode.row(node.path)}
          {...(isFolder && onFolderNativeFileDrop
            ? { 'data-import-drop-testid': testIds.localTextImport.folderDropTarget(node.path) }
            : {})}
          className={cn(
            'ws-nav-item ws-nav-row w-full min-w-0 max-w-full rounded-sm pr-2 py-1 text-sm select-none',
            'transition-[color,background-color,box-shadow,outline-color] duration-150 ease-out',
            isExact
              ? 'ws-nav-item--active text-sidebar-foreground'
              : isOpenElsewhere
                ? 'ws-nav-item--open'
                : isSelected
                  ? 'ws-nav-item--contains'
                  : 'text-sidebar-foreground/70',
            isNativeFileDropTarget && 'ws-nav-item--drop-target',
          )}
          style={{ '--ws-nav-pad-left': `${padLeft}px` } as React.CSSProperties}
          onContextMenu={e => {
            e.preventDefault()
            e.stopPropagation()
            onContextMenu?.(node, e)
          }}
          onDragEnter={
            isFolder && onFolderNativeFileDrop
              ? (e) => {
                  if (!e.dataTransfer.types.includes('Files')) return
                  e.preventDefault()
                  e.stopPropagation()
                  setIsNativeFileDropTarget(true)
                }
              : undefined
          }
          onDragOver={
            isFolder && onFolderNativeFileDrop
              ? (e) => {
                  if (!e.dataTransfer.types.includes('Files')) return
                  e.preventDefault()
                  e.stopPropagation()
                  e.dataTransfer.dropEffect = 'copy'
                  if (!isNativeFileDropTarget) setIsNativeFileDropTarget(true)
                }
              : undefined
          }
          onDragLeave={
            isFolder && onFolderNativeFileDrop
              ? (e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setIsNativeFileDropTarget(false)
                  }
                }
              : undefined
          }
          onDrop={
            isFolder && onFolderNativeFileDrop
              ? (e) => {
                  if (!e.dataTransfer.files?.length) return
                  e.preventDefault()
                  e.stopPropagation()
                  setIsNativeFileDropTarget(false)
                  onFolderNativeFileDrop(node.path, Array.from(e.dataTransfer.files))
                }
              : undefined
          }
        >
        {isFolder ? (
          <button
            type="button"
            tabIndex={canExpand ? 0 : -1}
            aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
            aria-expanded={canExpand ? expanded : undefined}
            disabled={!canExpand}
            className={cn(
              'ws-nav-row__leading',
              canExpand && 'ws-nav-row__leading--interactive'
            )}
            onClick={
              canExpand
                ? (e) => {
                    e.stopPropagation()
                    startTransition(() => toggleExpand(node.path))
                  }
                : undefined
            }
            onMouseDown={canExpand ? (e) => e.stopPropagation() : undefined}
          >
            {canExpand ? (
              <ChevronDown
                className="w-3 h-3 transition-transform duration-200 ease-out"
                style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
              />
            ) : null}
          </button>
        ) : null}
        <button
          type="button"
          className="ws-nav-row__body"
          title={
            node.type === 'folder' && onDrillFocus
              ? 'Double-click to focus folder'
              : undefined
          }
          onClick={handleRowClick}
          onDoubleClick={handleRowDoubleClick}
        >
          <span className="ws-nav-row__icon">
            {isFolder ? (
              <FolderIcon className="w-3.5 h-3.5" style={{ color: wsColor + 'cc' }} />
            ) : (
              fileIcon
            )}
          </span>
          {nameEl}
        </button>
        </div>
      </div>
      {expanded ? (
      <div
        className={cn('ws-nav-expand-panel', 'ws-nav-expand-panel--open')}
        style={{ gridTemplateRows: '1fr' }}
      >
        <div className="ws-nav-expand-panel__inner">
          {activeLevelFocus ? (
            <BrowseLevelFocusZone coordinator={activeLevelFocus}>
              {node.children?.map((child, i) => (
                <Fragment key={child.path}>{renderTreeChild(child, activeLevelFocus, i)}</Fragment>
              ))}
            </BrowseLevelFocusZone>
          ) : (
            node.children?.map((child, i) => (
              <Fragment key={child.path}>{renderTreeChild(child, null, i)}</Fragment>
            ))
          )}
          {animateBrowseChildrenReveal ? (
            <div
              className={cn(
                'browse-show-hidden-shell',
                showHiddenShellVisible && 'browse-show-hidden-shell--visible',
                showHiddenLinkExiting && 'browse-show-hidden-shell--exiting'
              )}
              aria-hidden={!showHiddenShellVisible}
            >
              <div className="browse-show-hidden-shell__inner">
                <button
                  type="button"
                  className="browse-show-hidden-link"
                  disabled={showHiddenLinkExiting !== null}
                  style={
                    {
                      '--browse-show-hidden-pad-left': `${wsNavRowPadLeftPx(
                        depth + 1,
                        false,
                        Boolean(activeLevelFocus),
                        activeLevelFocus?.gutterLeftPx,
                        padLeft
                      )}px`,
                    } as React.CSSProperties
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    revealParentAnimated()
                  }}
                >
                  <Eye className="h-3 w-3 shrink-0" />
                  {showHiddenLabel}
                </button>
              </div>
            </div>
          ) : (
            showHiddenLink && (
              <button
                type="button"
                className="browse-show-hidden-link"
                style={
                  {
                    '--browse-show-hidden-pad-left': `${wsNavRowPadLeftPx(
                      depth + 1,
                      false,
                      Boolean(activeLevelFocus),
                      activeLevelFocus?.gutterLeftPx,
                      padLeft
                    )}px`,
                  } as React.CSSProperties
                }
                onClick={(e) => {
                  e.stopPropagation()
                  revealParentAnimated()
                }}
              >
                <Eye className="h-3 w-3 shrink-0" />
                {showHiddenLabel}
              </button>
            )
          )}
        </div>
      </div>
      ) : null}
    </div>
  )
})
